import { Inject, Injectable, Optional } from "@angular/core";
import { TradingEntitlements, Account, OrderType, AccountType, OrderDuration, CommType, TradingOrderBase } from "../../models";
import { ORDER_ACTIONS, getValueOrEmptyString, isAveragePriceAccount, setShowCatEntity } from "../../utils";
import { TranslateService } from "@ngx-translate/core";
import { DatePipe, registerLocaleData } from "@angular/common";
import es from "@angular/common/locales/es";
import moment_tz_ from 'moment-timezone';


const moment = moment_tz_;


const linebreak = (navigator.appVersion.indexOf("Win") != -1)?'\r\n':'\n';

@Injectable()
export class EquityTradingUtils {
    commaDelimiterRE: RegExp = /\B(?=(\d{3})+(?!\d))/g;
    datePipe: DatePipe = new DatePipe('en-US');

    constructor(private translateService: TranslateService, @Optional() @Inject('AppSettings') appSettings: any) {
        if (appSettings?.language === 'es') {
            registerLocaleData( es );
        }
    }
    

    public getOrderTitleStyleColor(orderTitleStyle:string):string {
        //FIXME -- most likely need to pull out to common library
        let color:string='black';
        if(orderTitleStyle === 'tr-OrderTitle_Buy') {
            color = '#008338';
        } else if(orderTitleStyle === 'tr-OrderTitle_Sell') {
            color = '#d81b60'
        }
        return color;    
    }

    public getAccountName(account:Account,accountName:string): string {
        return 'Account: ' + accountName +(!!account.ip?'   /   ' +' IP#: ' + getValueOrEmptyString(account.ipName) + account.ip:'');
    }

    public getOrderPlacedon(username:string, approvalRequired:boolean,orderDate:string,orderTime:string,orderAMPM:string,orderTimeZone:string): string {
        let _orderPlacedon = "";
        if (!!orderDate && orderTime && orderAMPM && orderTimeZone) {
            if (!approvalRequired) {
                _orderPlacedon = 'Order submitted successfully on ' + orderDate + ' at ' + orderTime + ' ' + orderAMPM + ' ' + orderTimeZone + (!!username?"/ Entered by " + username:'');
    
            } else {
                _orderPlacedon = 'Order submitted for approval on ' + orderDate + ' at ' + orderTime + ' ' + orderAMPM + ' ' + orderTimeZone + (!!username?"/ Entered by " + username:'');
            }
    
        } else {
            _orderPlacedon = (!!username?"Entered by " + username:'');
        }
        return _orderPlacedon;
    }

    public getOrderProcessing(isOrderSuccessfullySubmitted: boolean): string {
        let _orderProcessing = "";
        if (isOrderSuccessfullySubmitted) {
          _orderProcessing = "The following Order has been sent for Processing";
        }
        return _orderProcessing;
    }

    public getOrderStatus(isOrderSuccessfullySubmitted:boolean, hasViolations:boolean,extended:boolean): string {
        let _orderstatus = "";
        let ExtendedHours = "";
        if (extended) {
            ExtendedHours = ' Extended Hours ';
        }
        if (!isOrderSuccessfullySubmitted && !hasViolations) {
            _orderstatus = "Equity"+(!!ExtendedHours?ExtendedHours:' ')+"Order Review";
        } else if (!isOrderSuccessfullySubmitted && hasViolations) {
            _orderstatus = "Equity"+(!!ExtendedHours?ExtendedHours:' ')+"Order Review";
        } else if (isOrderSuccessfullySubmitted) {
            _orderstatus = "Equity"+(!!ExtendedHours?ExtendedHours:' ')+"Order Confirmation";
        }
        return _orderstatus;
    }


    
    public getTransactionType(transactionType: string): string {
        switch (transactionType?.trim()) {
            case "BUY": return "Buy";
            case "SELL_FROM_PORTFOLIO": return "Sell from Position";
            case "SELL_ALL": return "Sell All";
            case "SELL_SHORT": return "Sell Short";
            case "BUY_TO_COVER": return "Buy to Cover";
            case "SHORT_SALE_EXEMPT": return "Short EX Deem to Own";
            case "LIMIT": return "Limit";
            case "AON": return "Limit - All or None";
            case "MARKET": return "Market";
            case "STOP": return "Stop";
            case "STOP_LIMIT": return "Stop - Limit";
            default:
                return "";
        }
    };

    private splitRegExFixed(value:number) {
        let split:string[]=[];
        if (value === null || value === undefined) {
            return '';
        }
        if (value === 0 || !!value) {
          split = value.toFixed(2).split('.');
        }
        let split0 = '';
        let split1 = '';
        let decimal = '';
        if (!!split[0]) {
            split0 = split[0];
        }
        if (!!split[1]) {
          split1 = split[1];
          decimal = '.';
        }
        return (split0).replace(this.commaDelimiterRE, ',') + decimal + split1;
    }

    private splitRegExUnFixed(value:number) {
        let split:string[]=[];
        if (value === 0) {
            return '0.00';
        }
        if (value === null || value === undefined) {
            return '';
        }
        if (!!value) {
          split = value.toString().split('.');
        }
        let split0 = '';
        let split1 = '';
        let decimal = '';
        if (!!split[0]) {
            split0 = split[0];
        }
        if (!!split[1]) {
          split1 = split[1];
          decimal = '.';
        }
        return (split0).replace(this.commaDelimiterRE, ',') + decimal + split1;
    }

    public getOrderDescription(order: any, entitlements: TradingEntitlements): string {
        let _orderDescription = this.getTransactionType(order.transactionType);
        if (order.tradeInShares) {
            _orderDescription = _orderDescription + ' ' + this.splitRegExUnFixed(order.ordQty) + " Share(s) of " + order.quote.symId;
        } else {
            _orderDescription = _orderDescription + " $" + this.splitRegExFixed(order.dollarQty) + " " + order.quote.symId;
        }
        return _orderDescription;
    }

    public processRefNumTranslation() {
        let _refNum = '';

        this.translateService.get('messages.RefNum').subscribe((translation: string) => {
            _refNum+= translation;

        });
        return getValueOrEmptyString(_refNum);
    }

    public processRefNum(order: any, entitlements: TradingEntitlements) {
        if (!!order.chkgActyRefNum) {
            return getValueOrEmptyString(order.chkgActyRefNum);
        } else if (entitlements.isRetailUser && (!!order.trdOrdCnfrmCd)) {
            return getValueOrEmptyString(order.trdOrdCnfrmCd);
        }
    }

    public processStatus(status) {
        let _status = '';
        this.translateService.get('ORDER_STATUS.STATUS.' + (!!status? status:'EMPTY')).subscribe((translation: string) => {
            _status+= translation;
        });
        return getValueOrEmptyString(_status);
    }

    public getQuoteDescription(order: any): string {
        let _quoteDescription = "";
        if (!!order.quote?.description) {
            _quoteDescription = order.quote.description.toString().replace(/\t/g, '').split(linebreak).toString()
        }
        return _quoteDescription
    }
    
    public getQuoteDescriptionCusip(order: any): string {
        let _quoteDescriptionCusip = "";
        let _quoteDescription = "";
        if (!!order.quote?.cusipId) {
            _quoteDescriptionCusip = this.getQuoteDescription(order).trim() + " (" + order.quote.cusipId + ")"
        }
    
        return _quoteDescriptionCusip;
    }

    public getFormattedTrailer(order: any, entitlements:TradingEntitlements): string {
        let _orderTrailer = order.trailer === null ? '' : order.trailer;
        let _formattedTrailer = "";
        if(entitlements.isRiaUser && (!!order.trailer || !!order.trailerDiscExercised)) {

            if (order.trailerDiscExercised != null && order.trailerDiscExercised === 'Y') {
                _formattedTrailer = 'Discretion Exercised ' + _orderTrailer;
            } else if (order.trailerDiscExercised != null && order.trailerDiscExercised === 'N') {
                _formattedTrailer = 'Discretion Not Exercised ' + _orderTrailer;
            } else {
                _formattedTrailer = _orderTrailer;
            }
        }
        return _formattedTrailer;

    }

    public getQuantity(order: any): string {
        let _quantity = "";
        if (order.tradeInShares) {
            _quantity = this.splitRegExUnFixed(order.ordQty) + " Share(s)";
        } else {
            _quantity = "$" + this.splitRegExFixed(order.dollarQty);
        }
        return _quantity;
    }

    private showSolicited(entitlements: TradingEntitlements, account:Account): boolean {
        let showSolicited = false;
        if (entitlements.isBdUser) {
            showSolicited = true;
        } else if (entitlements.isRiaUser && account.ibdId === 'C01' && account.acctProdtProfileCd === 'PMAS') {
            showSolicited = true;
        } else if(entitlements.isRetailUser) {
            showSolicited = entitlements.isSolicitedEnabled || entitlements.isNonSolicitedEnabled;
        }
        return showSolicited;
    }

    public getSolicited(solicited: boolean, entitlements: TradingEntitlements, account: Account): string {
        let _solicited = "";
        if (this.showSolicited(entitlements,account)) {
            if (solicited) {
                _solicited = "Yes";
            } else {
                _solicited = "No";
            }
        }
        return _solicited;
    }

    public processDuration(duration: OrderDuration, gtdDate: string) {
        if (!!duration && duration === OrderDuration.GTD) {
            return 'GTDate - ' + gtdDate.replace(/-/gi,'/');
        } 

            switch(duration) {
                case OrderDuration.DAY:
                    return "Day";
                case OrderDuration.GTC:
                    return "Good till Cancelled (GTC)";
                case OrderDuration.PREMKT:
                    return "Pre-Market";
                case OrderDuration.AFTMKT:
                    return "After Market";
                case OrderDuration.OPG:
                    return "Execute at Opening (OPG)";
                case OrderDuration.IOC:
                    return "Execute Now or Cancel (IOC)";
                case OrderDuration.FOK:
                    return "Fill or Kill (FOK)";
                case OrderDuration.GTM:
                    return "Good till Month (GTM)";
                default:
                    return "";
            }
        
    }

    public processCommissionAmt(commType: string, commissionAmt: number) {
        let dollarCent = '';
        let commAmt = '';


        if (commType === 'COM' || commType === 'MUD') {
            dollarCent+='$';
        } else if (commType==='RTE') {
            dollarCent+= '\u00A2';
        }

        commAmt += dollarCent;

        if (commType === 'PFM' || commType === 'MLT') {
            commAmt += commissionAmt;
        } else {
            commAmt += this.splitRegExFixed(commissionAmt);
        }

        if (commType === 'PFM' || commType === 'MLT') {
            commAmt+='%';
        }
        return commAmt;
    }

    public getAccountType(order: any): string {
        let accountType;
        if (!!order.avgPrcAcct && !!order.avgPrcAcctTyp) {
            accountType = order.avgPrcAcctTyp;
        } else {
            accountType = order.accountType;
        }
        switch (accountType) {
            case AccountType.CASH:
                return "Cash";
            case AccountType.MARGIN:
                return "Margin";
            case AccountType.SHORT:
                return "Short";
            case AccountType.COD:
                return "COD";
            case AccountType.RETIREMENT:
                return "Retirement";
            case AccountType.PRINCIPAL:
                return "Principal";
            case AccountType.INCOME:
                return "Income";
            default:
                return "";
        }

    }

    public getAccountTypeEditOrder(account:Account,origOrder:any): string {
        let accountType;
        if (!!origOrder.avgPrcAcct && !!origOrder.avgPrcAcctTyp) {
            accountType = origOrder.avgPrcAcctTyp;
        } else {
            accountType = origOrder.accountType;
        }
        if (account.custodianCd === 'BNYM') {
            origOrder.accountType = origOrder.balTypeCd === 'I' ? AccountType.INCOME : AccountType.PRINCIPAL;
            accountType = origOrder.accountType;
        }
        switch (accountType) {
            case AccountType.CASH:
                return "Cash";
            case AccountType.MARGIN:
                return "Margin";
            case AccountType.SHORT:
                return "Short";
            case AccountType.COD:
                return "COD";
            case AccountType.RETIREMENT:
                return "Retirement";
            case AccountType.PRINCIPAL:
                return "Principal";
            case AccountType.INCOME:
                return "Income";
            default:
                return "";
        }
        
    }

    public getCommType(commType) {
        switch (commType) {
            case CommType.COM:
                return "Override";
            case CommType.MLT:
                return "Multiplier";
            case CommType.MUD:
                return "Markup / Markdown";
            case CommType.MUD_AUTO:
                return "Markup / Markdown - Auto";       
            case CommType.PFM:
                return "Percent of First Money"; 
            case CommType.RTE:
                return "Rate Per Share";    
            default:
                return "";
        }
    }

    public getAddlOrdData(addlOrdData: any): string {
        let _addlOrdData = "";
        if (!!addlOrdData) {
            for (let tradeCondition of addlOrdData) {
                _addlOrdData = _addlOrdData + this.getTradeCondition(tradeCondition) + ",";
            }
        }
        return _addlOrdData.substring(0,_addlOrdData.length-1);
    }
    
    private getTradeCondition(tradeCondition: string,): string {
        switch (tradeCondition?.trim()) {
            case "AON":
                return "All Or None";
            case "NOTHELD":
                return "Not Held";
            case "DONOTINCREASE":
                return "Do Not Increase Shares";
            case "DONOTREDUCE":
                return "Do Not Reduce Price";
            case "NEXTDAY":
                return "Next Day";
            case "CASHSAMEDAY":
                return "Cash - Same Day Settlement";
            case "EXECUTEASAGENT":
                return "Execute As Agent";
            default:
                return "";
        }
    }

    public getFloorInst(floorInst: string): string {
        switch (floorInst?.trim()) {
            case "10% POV": return "10% Percentage of Volume (POV)";
            case "15% POV": return "15% Percentage of Volume (POV)";
            case "25% POV": return "25% Percentage of Volume (POV)";
            case "33% POV": return "33% Percentage of Volume (POV)";
            case "EXECUTE HALF NOW,WRK HALF": return "Execute half now, work half";
            case "NO NEED TO COMPLETE": return "No need to complete";
            case "PLS WAIT FOR FURTHER INST": return "Please wait for further instructions";
            case "TWAP": return "Time Weighted Average Price (TWAP)";
            case "VWAP": return "Volume Weighted Average Price (VWAP)";
            case "VWAP OVER 2 HOURS": return "Volume Weighted Avg Price > 2 hours (VWAP)";
            case "VWAP OVER 3 HOURS": return "Volume Weighted Avg Price > 3 hours (VWAP)";
            case "WORK NOT HELD": return "Work Not Held";
            case "INSIDER ACCOUNT": return "Insider account";
            case "NON REPORT INSIDER/ASSOC": return "Non report insider/assoc";
            case "PROMOTOR INSIDER RELATION": return "Promotor Insider Relation";
            case "REPORTING INSIDER": return "Reporting insider";
            case "SIGNIFICANT SHRHLDR(>20%)": return "Significant Shareholder (>20%)";
            default:
                return "";
        }
    }

    public getOrderType(orderType): string {
        switch (orderType) {
            case OrderType.MARKET:
                return "Market";
            case OrderType.LIMIT:
                return "Limit";
            case OrderType.STOP:
                return "Stop";
            case OrderType.AON:
                return "AON";
            case OrderType.FOK:
                return "FOK";
            case OrderType.STOP_LIMIT:
                return "Stop Limit";
            case OrderType.STOP_LIMIT_AON:
                return "Stop Limit AON";
            case OrderType.TRAILING_STOP_DOLLAR:
                return "Trailing Stop $";
            case OrderType.TRAILING_STOP_PCT:
                return "Trailing Stop %";
            case OrderType.TRAILING_STOP_LIMIT_DOLLAR:
                return "Trailing Stop Limit $";
            case OrderType.TRAILING_STOP_LIMIT_PCT:
                return "Trailing Stop Limit %";
            case OrderType.TRAILING_STOP_LIMIT_AON:
                return "Trailing Stop Limit AON";
            case OrderType.TRAILING_STOP_LIMIT_PCT_AON:
                return "Trailing Stop Limit % AON";
            case OrderType.EXECUTE_AT_CLOSE:
                return "Execute at Close";
            case OrderType.LIMIT_ON_CLOSE:
                return "Limit on Close";
            case OrderType.OR_BETTER:
                return "Or Better";
            case OrderType.WOW:
                return "WOW";    
            case OrderType.NET_CREDIT:
                return "Net Credit";  
            case OrderType.NET_DEBIT:
                return "Net Debit";
            case OrderType.EVEN:
                return "Even";                 
            default:
                return "";
        }
    }

    public getSourceOfInput(sourceOverride: string): string {
        switch (sourceOverride?.trim()) {
            case "3":
                return "NetX360";
            case "I":
                return "Web";
            case "J":
                return "Interactive Voice Response";
            case "K":
                return "Proprietary";
            case "Z":
                return "Home Office";
            case "Q":
                return "Wireless Client via NX";
            case "R":
                return "Wireless Pro via NX";
            case "A":
                return "External Advisor Site";
            case "5":
                return "External Mobile Site";
            default:
                return "";
        }
    }

    formatBackendTime(sourceTime: string): string { //aabbcccccc -> aa:bb:ccccc
        if ( !!sourceTime && sourceTime !== '' ) {
            return sourceTime.substring(0, 2) + ':' + sourceTime.substring(2, 4) + ':' + sourceTime.substring(4);
        } else {
            return sourceTime;
        }
    }

    formatBackendDate(sourceDate: string, origOrder?:TradingOrderBase): string {     // yyyymmdd => mm/dd/yyyy
        if (!!sourceDate && !sourceDate.includes('/')) {
            return sourceDate.substring(4, 6) + '/' + sourceDate.substring(6, 8) + '/' + sourceDate.substring(0, 4);
        } else if(!!sourceDate){
            return sourceDate
        } else {
            return '';
        }
      }

    public getDiscExercised(discExercised: string, entitlements: TradingEntitlements): string {
        let _discExercised = "";
        if (!entitlements.isRetailUser) { 
            if (discExercised === "Y") {
                _discExercised = "Yes";
            } else if (discExercised === "N"){
                _discExercised = "No";
            }
        } 
        return getValueOrEmptyString(_discExercised);

    }

    public getDeskRoute(oatsDeskTypeCode: string): string {
        switch (oatsDeskTypeCode?.trim()) {
            case "A": return "Agency(A)";
            case "AR": return "Arbitrage(AR)";
            case "B": return "Block Trading(B)";
            case "C": return "Convertibles Desk(C)";
            case "D": return "Derivatives(D)";
            case "IS": return "Institutional(IS)";
            case "IN": return "International(IN)";
            case "O": return "Other(O)";
            case "PF": return "Preferred(PF)";
            case "PT": return "Program Trading(PT)";
            case "PR": return "Proprietary(PR)";
            case "S": return "Sales(S)";
            case "T": return "Trading(T)";
            case "TR": return "Treasury(TR)";
            case "SW": return "Swaps(SW)";
            case "EC": return "Eqty Capital Mkts(EC)";
            case "CR": return "Ctrl Risk Books(CR)";
            default:
                return "";
        }
    }

    public getPreparedBy(username:string): string {
        let today = new Date();
        let date = moment(today).tz('America/New_York').format('MMMM D, YYYY, h:mm:ss A [ET]');
        let _preparedBy:string;

        if(!!username) {
            _preparedBy = "Prepared by "+ username +" on " + date ;  //P5CREWID at 04/22/2022 02:32:20 PM ET

        } else {
            _preparedBy = 'Prepared on ' + date;
        }
        
        return _preparedBy;
    }

    public getPaInd(order:any,entitlements:TradingEntitlements,account:Account) {
        let _paInd = '';
        if(this.showPaInd(order,entitlements,account)) {
            if (order.paInd?.toUpperCase()==='A') {
                _paInd = 'Agent'
            } else if (order.paInd?.toUpperCase()==='P') {
                _paInd = 'Principal'
            } else {
                _paInd = 'Agent';
            }
        }
        return _paInd
    }

    private showPaInd(order:any,entitlements:TradingEntitlements,account:Account):boolean {
        return (entitlements.isBdUser && 
            (account?.acctClassificationCd && account.acctClassificationCd.toUpperCase() === 'OM') && 
            !(order.quote && order.quote.qtIntlInd === 'Y')) 
    }

    public setShowPDFCatEntity(account:Account,entitlements:TradingEntitlements,order:any,origOrder:any,show:boolean) {
        //FIXME -- pull this out to the pdf no need for this smnall wrapper, except to maybe unit test
        return setShowCatEntity(isAveragePriceAccount(account.acctNum), account.acctClassificationCd, entitlements.isEntitledtToShowCatEntity, show) && ((!!order.fdid && !!order.fdid.trim()) || (!!origOrder?.fdid && !!origOrder?.fdid.trim()))
    }

}